import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Compass, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { CONTACT_INFO } from "@/data/companies";

const pillars = [
  {
    icon: Building2,
    title: "A holding company, not a conglomerate",
    body: "We own and operate a focused group of service businesses, giving each the autonomy to excel while sharing a common foundation of trust.",
  },
  {
    icon: Compass,
    title: "Rooted in Alberta",
    body: "Based in Edmonton, we understand the businesses and communities we serve because we're part of them.",
  },
  {
    icon: Target,
    title: "Built for the long term",
    body: "Our goal isn't quick wins. It's dependable companies that earn their reputation one relationship at a time.",
  },
];

export default function About() {
  return (
    <div>
      <section className="bg-slate-50 pt-32 md:pt-44 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">
              About Us
            </p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight max-w-3xl">
              A trustworthy Alberta business, owning businesses that matter
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              AMS Venture Holdings Inc. is a Canadian holding company based in
              Edmonton, Alberta. We own and operate specialized service
              companies across ATM placement, professional moving, and
              website & digital services.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 -mt-4 md:-mt-6">
        <Reveal>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgb(0,0,0,0.06)]">
            <img
              src={CONTACT_INFO.aboutBanner}
              alt="Canadian business district"
              className="h-64 md:h-96 w-full object-cover"
            />
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                  <p.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold tracking-tight text-slate-900">
                  {p.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Our approach
            </h2>
            <div className="mt-6 space-y-5 text-lg text-slate-600 leading-relaxed">
              <p>
                We believe great service comes from doing the fundamentals
                exceptionally well: showing up on time, communicating clearly,
                and standing behind our work. That philosophy shapes every
                company under the AMS umbrella.
              </p>
              <p>
                By keeping our businesses specialized and locally focused, we
                stay close to the people we serve, delivering the responsiveness
                of a local team with the reliability of an established
                organization.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white px-8 py-14 md:px-16 md:py-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                Want to work with us?
              </h2>
              <p className="mt-3 text-slate-600 text-lg">
                We&apos;d love to hear about your needs.
              </p>
            </div>
            <Button
              asChild
              data-testid="about-contact-cta"
              className="h-12 rounded-full bg-slate-900 hover:bg-blue-600 text-white px-7 text-base transition-colors duration-300 shrink-0"
            >
              <Link to="/contact">
                Contact Us <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
